import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router'
import { Plus } from 'lucide-react'
import isError from 'lodash/isError'
import isNumber from 'lodash/isNumber'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_ORDER,
  PERMISSION_ENTITY_SOURCE_DOCUMENT,
  ROUTE_CUSTOMERS,
  buildNavPathTrader
} from '@klear/constants'
import { KlearDate } from '@klear/date'
import type { ReceivableTab } from '@klear/invoice-offer/receivables'
import { RECEIVABLE_DEFAULT_TERMS_DAYS } from '@klear/order-event/receivable'
import { Money } from '@klear/money'
import { useCustomers } from '@klear/customer-ui/use-customers'
import { FileRow } from '@klear/ui/components/document-upload-page'
import { FileDropZone } from '@klear/ui/components/file-drop-zone'
import { UPLOAD_ACCEPTED_LABEL, UPLOAD_MANUAL_ENTRY_NOTE, UPLOAD_ACCEPT_LIST } from '@klear/upload/upload-formats'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { Combobox } from '@klear/ui/components/combobox'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { Modal } from '@klear/ui/components/modal'
import { MoneyInput } from '@klear/ui/components/money-input'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import { toast } from '@klear/ui/hooks/use-toast'
import { trpc } from '@klear/ui/lib/trpc'
import { cn } from '@klear/ui/lib/utils'
import { withReturnUrl } from '@klear/ui/wizard/return-url'
import { useSendForReview } from '@klear/ui/hooks/use-document-upload-request'

const REOPEN_QUERY = 'newReceivable'
const REOPEN_MANUAL = 'manual'
const MANUAL_RECEIVABLE_TAB: ReceivableTab = 'awaitingQuote'

type Mode = 'upload' | 'manual'

export function NewReceivableButton({ traderId }: { traderId: number }) {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [mode, setMode] = useState<Mode | null>(null)

  useEffect(() => {
    if (searchParams.get(REOPEN_QUERY) !== REOPEN_MANUAL) return
    setMode('manual')
    const next = new URLSearchParams(searchParams)
    next.delete(REOPEN_QUERY)
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  if (!user?.hasKlearRole) return null

  return (
    <>
      <Button variant="outline" onClick={() => setMode('upload')}>
        <Plus className="mr-2 h-4 w-4" />
        Add receivable
        <Badge variant="secondary" className="ml-2 text-[10px]">
          Klear only
        </Badge>
      </Button>
      {mode && (
        <NewReceivableDialog traderId={traderId} mode={mode} onModeChange={setMode} onClose={() => setMode(null)} />
      )}
    </>
  )
}

function NewReceivableDialog({
  traderId,
  mode,
  onModeChange,
  onClose
}: {
  traderId: number
  mode: Mode
  onModeChange: (mode: Mode) => void
  onClose: () => void
}) {
  const { user } = useAuth()
  const canUpload = !!user?.hasPermission(PERMISSION_ENTITY_SOURCE_DOCUMENT, PERMISSION_ACTION_ADD)
  const canCreateManually =
    !!user?.hasPermission(PERMISSION_ENTITY_ORDER, PERMISSION_ACTION_ADD) &&
    !!user?.hasPermission(PERMISSION_ENTITY_INVOICE, PERMISSION_ACTION_ADD)
  const upload = useUploadReceivable(traderId, onClose)
  const manual = useManualReceivable(traderId, onClose)
  const isManual = mode === 'manual' && canCreateManually
  const active = isManual ? manual : upload
  const isBusy = upload.isBusy || manual.isBusy

  return (
    <Modal
      size="sm"
      open
      onOpenChange={(next) => {
        if (!next && !isBusy) onClose()
      }}
      title="New receivable"
      description={active.description}
      primaryText={active.primaryText}
      primaryDisabled={active.primaryDisabled}
      secondaryText="Cancel"
      secondaryDisabled={isBusy}
      handleSecondaryAction={onClose}
      handlePrimaryAction={active.submit}
    >
      <div className="grid gap-3">
        <div className={cn(isManual && 'hidden')}>{upload.body}</div>
        {canCreateManually && <div className={cn(!isManual && 'hidden')}>{manual.body}</div>}
        {!isManual && canCreateManually && (
          <ModeLink disabled={isBusy} onClick={() => onModeChange('manual')}>
            I don&apos;t have documents yet
          </ModeLink>
        )}
        {isManual && canUpload && (
          <ModeLink disabled={isBusy} onClick={() => onModeChange('upload')}>
            Upload documents instead
          </ModeLink>
        )}
      </div>
    </Modal>
  )
}

function ModeLink({
  disabled,
  onClick,
  children
}: {
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="justify-self-start text-sm underline underline-offset-4 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  )
}

function useUploadReceivable(traderId: number, onClose: () => void) {
  const { send, statuses, isUploading, reset } = useSendForReview()
  const [files, setFiles] = useState<File[]>([])
  const hasFailures = statuses.some((row) => row.status === 'error')

  const submit = async () => {
    if (files.length === 0) return
    setFiles([])
    const failed = await send(files, traderId, { traderSide: 'sells' })
    if (failed.length > 0) {
      setFiles(failed.map((row) => row.file))
      toast({
        title: `${failed.length} of ${files.length} ${files.length === 1 ? 'file' : 'files'} did not upload`,
        description: 'Only those files are still selected. Submit again to retry them.',
        variant: 'destructive'
      })
      return
    }
    toast({
      title: 'Documents received',
      description: `Klear will review ${files.length === 1 ? 'the document' : `the ${files.length} documents`}. The receivable shows here once Klear files it.`
    })
    onClose()
  }

  const count = `${files.length} ${files.length === 1 ? 'file' : 'files'}`
  const label = files.length === 0 ? 'Submit' : hasFailures ? `Retry ${count}` : `Submit ${count}`

  return {
    description:
      'Upload one or more documents for a sale: POs, invoices, proofs of acceptance. Klear reads them, sets up the order, customer and invoice, and sends you a quote.',
    primaryText: isUploading ? 'Uploading…' : label,
    primaryDisabled: files.length === 0 || isUploading,
    isBusy: isUploading,
    submit: () => void submit(),
    body: (
      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="new-receivable-files">Documents</Label>
          <FileDropZone
            id="new-receivable-files"
            files={files}
            onFilesChange={(next) => {
              reset()
              setFiles(next)
            }}
            accept={UPLOAD_ACCEPT_LIST}
            acceptedLabel={UPLOAD_ACCEPTED_LABEL}
            acceptedNote={UPLOAD_MANUAL_ENTRY_NOTE}
            disabled={isUploading}
          />
        </div>
        {statuses.length > 0 && (
          <ul className="rounded-md border px-3">
            {statuses.map((row, index) => (
              <FileRow key={`${row.file.name}-${index}`} row={row} />
            ))}
          </ul>
        )}
      </div>
    )
  }
}

function useManualReceivable(traderId: number, onClose: () => void) {
  const { customers } = useCustomers(traderId)
  const [, setSearchParams] = useSearchParams()
  const create = trpc.receivable.createWithoutDocuments.useMutation()
  const invalidate = useOrderEventInvalidate()
  const [customerId, setCustomerId] = useState('')
  const [amount, setAmount] = useState<Money>(Money.zero())
  const [invoiceDate, setInvoiceDate] = useState<string>(KlearDate.today().toJSON())

  const customerOptions = useMemo(
    () =>
      customers
        .filter((customer) => isNumber(customer.id))
        .map((customer) => ({ value: toString(customer.id), label: customer.name })),
    [customers]
  )
  const isSaving = create.isPending
  const canSave = customerId !== '' && amount.isPositive() && KlearDate.parse(invoiceDate) != null && !isSaving

  const save = async () => {
    if (!canSave) return
    try {
      await create.mutateAsync({
        traderId,
        customerId: toInteger(customerId),
        amount: amount.toJSON(),
        effectiveDate: invoiceDate
      })
      await invalidate()
      const customerName = customerOptions.find((option) => option.value === customerId)?.label ?? 'the customer'
      toast({
        title: 'Receivable created',
        description: `An invoice for ${customerName} is set up on Net ${RECEIVABLE_DEFAULT_TERMS_DAYS}. It shows under Awaiting quote, then moves to Quotes once Klear prices it.`
      })
      setSearchParams({ tab: MANUAL_RECEIVABLE_TAB }, { replace: true })
      onClose()
    } catch (error) {
      toast({
        title: 'Could not create the receivable',
        description: isError(error) ? error.message : 'Try again.',
        variant: 'destructive'
      })
    }
  }

  const { pathname } = useLocation()
  const createCustomerHref = withReturnUrl(
    buildNavPathTrader(toString(traderId), ROUTE_CUSTOMERS, 'new'),
    `${pathname}?tab=${MANUAL_RECEIVABLE_TAB}&${REOPEN_QUERY}=${REOPEN_MANUAL}`
  )

  return {
    description:
      'No documents yet? Tell Klear who the sale is to, how much and when. You can upload documents after you accept the quote.',
    primaryText: isSaving ? 'Creating…' : 'Create receivable',
    primaryDisabled: !canSave,
    isBusy: isSaving,
    submit: () => void save(),
    body: (
      <div className="grid gap-4">
        <div className="grid gap-1.5">
          <Label>Customer</Label>
          <Combobox
            value={customerId}
            options={customerOptions}
            placeholder="Search customers…"
            triggerLabel="Select customer…"
            onChange={setCustomerId}
          />
          <Link
            to={createCustomerHref}
            className="inline-flex h-auto items-center p-0 text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
          >
            <Plus className="h-3 w-3 mr-1" />
            Create customer
          </Link>
        </div>
        <MoneyInput label="Amount" value={amount} onChange={setAmount} disabled={isSaving} />
        <div className="grid gap-1.5">
          <Label htmlFor="new-receivable-date">Invoice date</Label>
          <Input
            id="new-receivable-date"
            type="date"
            value={invoiceDate}
            disabled={isSaving}
            onChange={(event) => setInvoiceDate(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            The invoice date, or when you expect to invoice. Terms start at Net {RECEIVABLE_DEFAULT_TERMS_DAYS}. A
            placeholder purchase order holds the invoice until yours arrives.
          </p>
        </div>
      </div>
    )
  }
}
